// Save a program as a tenant template: key + name → POST
// /programs/{id}/save-as-template. Re-saving an existing key creates a new
// version. Tenant-specific facets (department ids, people) cannot travel in a
// template — the server drops them and keeps a text hint per session.
import { useT } from '../lib/i18n';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/features/shared/components/ui/dialog';
import { Button } from '@/features/shared/components/ui/button';
import { Input } from '@/features/shared/components/ui/input';
import { Label } from '@/features/shared/components/ui/label';
import type { Program, ProgramTemplate } from '@/services/learningService';
import { useSaveProgramAsTemplate } from '../api/hooks';

export const templateKeyOf = (name: string) => name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');

interface Props { program: Program | null; onClose: () => void; onSaved?: (template: ProgramTemplate) => void }

export const SaveAsTemplateDialog: React.FC<Props> = ({ program, onClose, onSaved }) => {
  const t = useT();
  const save = useSaveProgramAsTemplate();
  return (
    <Dialog open={!!program} onOpenChange={(o) => { if (!o && !save.isPending) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('training:tpl_save_title', 'Save as template')}</DialogTitle>
          <DialogDescription>{t('training:tpl_save_help', 'Sessions (as day offsets), tracks, practical exercises, completion policies and the readiness policy become a reusable template for your company. Departments and specific people are not stored — they are kept as audience hints you set again when using the template.')}</DialogDescription>
        </DialogHeader>
        {program && (
          <SaveForm key={program.id} program={program} busy={save.isPending} onClose={onClose}
            onConfirm={(body) => save.mutate({ id: program.id, body }, { onSuccess: (tpl) => { toast.success(t('training:tpl_saved', 'Template saved — {{name}} v{{v}}', { name: tpl.name, v: tpl.version })); onSaved?.(tpl); onClose(); } })} />
        )}
      </DialogContent>
    </Dialog>
  );
};

const SaveForm: React.FC<{ program: Program; busy: boolean; onClose: () => void; onConfirm: (b: { key: string; name?: string }) => void }> = ({ program, busy, onClose, onConfirm }) => {
  const t = useT();
  const [key, setKey] = useState(templateKeyOf(program.name));
  const [name, setName] = useState(program.name);
  const keyOk = /^[a-z0-9][a-z0-9_-]*$/.test(key);
  return (
    <div className="space-y-3" data-testid="save-as-template-dialog">
      <div className="space-y-1">
        <Label htmlFor="sat-key" className="text-xs">{t('training:tpl_key', 'Template key')}</Label>
        <Input id="sat-key" className="h-9 font-mono" value={key} onChange={(e) => setKey(e.target.value)} disabled={busy} />
        <p className="text-[11px] text-muted-foreground">{t('training:tpl_key_help', 'Lowercase letters, digits, "_" or "-". Saving with an existing key creates a new version of that template.')}</p>
      </div>
      <div className="space-y-1">
        <Label htmlFor="sat-name" className="text-xs">{t('training:tpl_name', 'Template name')}</Label>
        <Input id="sat-name" className="h-9" value={name} onChange={(e) => setName(e.target.value)} disabled={busy} />
      </div>
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onClose} disabled={busy}>{t('training:cancel', 'Cancel')}</Button>
        <Button size="sm" disabled={!keyOk || busy} data-testid="save-as-template-confirm" onClick={() => onConfirm({ key, name: name.trim() || undefined })}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t('training:save', 'Save')}
        </Button>
      </div>
    </div>
  );
};

export default SaveAsTemplateDialog;
